import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, PageHeader, SectionCard, Spinner, Toggle } from '../components/ui';
import { fmtDate } from '../lib/format';
import { ROLE_LABELS, type Role } from '../lib/roles';

interface CrmUser {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  active: boolean;
  createdAt: number;
  updatedAt: number;
  lastLoginAt: number | null;
  permissions: string[];
}

const ROLES: Role[] = ['super_admin', 'admin', 'dealer_manager', 'viewer', 'affiliate'];

export function TeamPage() {
  const [items, setItems] = useState<CrmUser[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => api.get<{ items: CrmUser[] }>('/api/crm/crm-users').then((r) => setItems(r.items));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        title="Команда CRM"
        subtitle="Сотрудники с доступом к панели"
        actions={
          <button className="btn btn-gold" onClick={() => setShowCreate(true)}><Icon.Plus size={14} />Новый сотрудник</button>
        }
      />
      <SectionCard>
        {!items ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-4"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-500 py-4">Сотрудников нет</div>
        ) : (
          <div className="overflow-x-auto -mx-3">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Логин</th>
                  <th>Роль</th>
                  <th>Активен</th>
                  <th>Создан</th>
                  <th>Последний вход</th>
                  <th className="text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.displayName}</td>
                    <td className="font-mono text-xs">{u.username}</td>
                    <td>
                      <select
                        className="input !py-1.5 !text-xs w-44"
                        value={u.role}
                        onChange={async (e) => {
                          await api.patch(`/api/crm/crm-users/${u.id}`, { role: e.target.value });
                          load();
                        }}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    </td>
                    <td>
                      <Toggle pressed={u.active} onChange={async (v) => {
                        await api.patch(`/api/crm/crm-users/${u.id}`, { active: v });
                        load();
                      }} ariaLabel="Активен" />
                    </td>
                    <td className="text-xs text-stone-400">{fmtDate(u.createdAt)}</td>
                    <td className="text-xs text-stone-400">{u.lastLoginAt ? fmtDate(u.lastLoginAt) : <span className="text-stone-500">никогда</span>}</td>
                    <td className="text-right">
                      <button
                        className="btn btn-danger !px-2.5 !py-1.5"
                        onClick={async () => {
                          if (!confirm(`Удалить сотрудника ${u.username}?`)) return;
                          await api.del(`/api/crm/crm-users/${u.id}`);
                          load();
                        }}
                      >
                        <Icon.Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post('/api/crm/crm-users', { username, password, displayName, role });
      onCreated();
    } catch (e: any) {
      setErr(e?.error === 'USERNAME_TAKEN' ? 'Логин уже занят' : 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 anim-fade" onClick={onClose}>
      <div className="glass-strong rounded-2xl p-6 w-full max-w-md anim-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Новый сотрудник</h2>
          <button onClick={onClose} className="p-1 rounded-md text-stone-400 hover:text-white hover:bg-white/5"><Icon.Close size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label">Имя для отображения</label>
            <input className="input mt-1.5" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </div>
          <div>
            <label className="label">Логин (3–40)</label>
            <input className="input mt-1.5" value={username} onChange={(e) => setUsername(e.target.value)} required pattern="[A-Za-z0-9_.\-]{3,40}" />
          </div>
          <div>
            <label className="label">Пароль (≥ 8)</label>
            <input className="input mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
          </div>
          <div>
            <label className="label">Роль</label>
            <select className="input mt-1.5" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          {err && <div className="rounded-lg border border-red-400/20 bg-red-500/10 text-red-300 px-3 py-2 text-sm">{err}</div>}
          <button className="btn btn-gold w-full" disabled={busy}>
            {busy ? 'Создаём…' : 'Создать'}
          </button>
        </form>
      </div>
    </div>
  );
}
